import React from 'react';
import { string, number, bool, func, shape, arrayOf } from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullseye, faUser, faCarSide, faUsers, faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';
import GmproRejectSheet from './GmproRejectSheet';
import { MapIcon, HourglassIcon } from './HeroIcons';
import { ETA } from './gmproTokens';

// Left edge of the second column (fleet + occupancy in the header, address in
// the rows), measured from the design: name block 140px + 80px gap = 220px.
// The fleet name and the passenger addresses share this edge.
const COL_ONE = 220;

// The recommendation id formatted like the design's route id (A-000001): the
// proposal's display letter followed by its zero-padded id.
const formatId = (letter, id) => `${letter || 'A'}-${String(id).padStart(6, '0')}`;

// The coordinates of a trip's relevant pin (origin for inbound, destination
// for outbound).
const pinFor = (trip) =>
  trip.trip_type === 'outbound'
    ? { lat: trip.destination_latitude, lng: trip.destination_longitude }
    : { lat: trip.location_latitude, lng: trip.location_longitude };

// Split a Google-format address ("street, suburb, city, postal, country") into
// the Area/Suburb (the second comma part) and the rest of the line. Operators
// scan by suburb, so it leads the row before a pipe; the rest can truncate.
const splitAddress = (address) => {
  if (!address) return { area: '', rest: '' };
  const parts = address.split(',').map((part) => part.trim()).filter(Boolean);
  if (parts.length < 2) return { area: '', rest: address };
  const area = parts[1];
  const rest = [parts[0], ...parts.slice(2)].join(', ');
  return { area, rest };
};

// One passenger's address: "Suburb | rest of line". On row hover a circular
// copy button fades in on the far right; the address line truncates to make
// room for it so the two stay inline.
const AddressCell = ({ address }) => {
  const [hovered, setHovered] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const { area, rest } = splitAddress(address);

  // Copy the full, unsplit address and flash a tick for a second.
  const handleCopy = (event) => {
    event.stopPropagation();
    if (navigator.clipboard) {
      navigator.clipboard.writeText(address);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1000);
  };

  // Keyboard parity for the hover-reveal copy button: Enter/Space toggle the
  // hovered state so keyboard users can reach the copy affordance too.
  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setHovered((previous) => !previous);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className="flex items-center"
      // Reserve the copy button's height in both states so revealing it on
      // hover never changes the row height (no vertical jump).
      style={{ flex: 1, minWidth: 0, gap: 8, minHeight: 20 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      onKeyDown={handleKeyDown}>
      {/* Figma renders the whole line as one uniform Ubuntu Regular 14px
          #6e6c71 string — the suburb and pipe carry no special weight or colour. */}
      <span className="truncate" style={{ minWidth: 0 }}>
        {area ? `${area} | ${rest}` : rest}
      </span>
      {hovered && (
        <button
          data-testid="gmpro-copy-address"
          onClick={handleCopy}
          title={copied ? 'Copied' : 'Copy address'}
          className="flex items-center justify-center focus:outline-none"
          style={{
            width: 20,
            height: 20,
            flexShrink: 0,
            borderRadius: '50%',
            background: '#FFFFFF',
            border: `1px solid ${ETA.cardBorder}`,
            color: copied ? ETA.confirm : ETA.darkGrey,
            boxShadow: '0px 0px 2px rgba(0,0,0,0.15)',
          }}>
          <FontAwesomeIcon icon={copied ? faCheck : faCopy} style={{ fontSize: 11 }} />
        </button>
      )}
    </div>
  );
};

AddressCell.propTypes = {
  address: string,
};

// Route metrics formatting: metres to one-decimal km; seconds to whole minutes
// under an hour, otherwise "Hh Mm" (dropping the minutes when they're zero).
const formatKm = (meters) => `${(meters / 1000).toFixed(1)} km`;
const formatDuration = (seconds) => {
  const totalMinutes = Math.round(seconds / 60);
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
};

// Route distance + duration pill in the card header (Figma node 2852:39472): an
// #F8F7F9 chip (pl-10/pr-15, 7px radius) with an inset shadow, a map icon +
// distance and an hourglass icon + duration in purple Ubuntu Medium 12px. The
// two metric groups have an 8px icon-to-text gap and sit 4px apart, matching the
// design. Hidden when the run carries no metrics.
const RouteMetricsBadge = ({ distanceMeters, durationSeconds }) => {
  if (distanceMeters == null && durationSeconds == null) return null;

  const metric = (icon, text) => (
    <span
      className="flex items-center"
      style={{ gap: 8, color: ETA.purple, fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap' }}>
      {icon}
      {text}
    </span>
  );

  return (
    <div
      data-testid="gmpro-route-metrics"
      className="flex items-center"
      style={{
        height: 32,
        paddingLeft: 10,
        paddingRight: 15,
        borderRadius: 7,
        gap: 4,
        flexShrink: 0,
        background: ETA.body,
        boxShadow: 'inset 0px 0px 2.5px 0px rgba(0,0,0,0.12)',
      }}>
      {distanceMeters != null &&
        metric(<MapIcon size={14} style={{ color: ETA.purple }} />, formatKm(distanceMeters))}
      {durationSeconds != null &&
        metric(<HourglassIcon size={14} style={{ color: ETA.purple }} />, formatDuration(durationSeconds))}
    </div>
  );
};

RouteMetricsBadge.propTypes = {
  distanceMeters: number,
  durationSeconds: number,
};

// The Approve action. When the recommended driver is unavailable the button is
// disabled and a hover tooltip explains why and what to do. The tooltip is a real
// element (not a native `title`, which browsers suppress on disabled buttons) so
// the explanation actually shows. The wrapping span owns the hover so it fires
// even while the button itself is disabled.
const ApproveButton = ({ disabled, onClick, style }) => {
  const [hovered, setHovered] = React.useState(false);

  return (
    <span
      className="inline-flex"
      style={{ position: 'relative' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      <button
        data-testid="gmpro-approve-btn"
        onClick={onClick}
        disabled={disabled}
        className="flex items-center text-white focus:outline-none"
        style={{ ...style, opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}>
        Approve
      </button>
      {disabled && hovered && (
        <span
          role="tooltip"
          data-testid="gmpro-approve-tooltip"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            zIndex: 20,
            width: 230,
            padding: '8px 10px',
            background: ETA.darkGrey,
            color: '#FFFFFF',
            fontSize: 12,
            fontWeight: 400,
            lineHeight: 1.4,
            borderRadius: 6,
            boxShadow: '0px 2px 6px rgba(0,0,0,0.2)',
            whiteSpace: 'normal',
          }}>
          The recommended driver is unavailable for this grouping. Reject the driver or the grouping to proceed.
        </span>
      )}
    </span>
  );
};

ApproveButton.propTypes = {
  disabled: bool,
  onClick: func,
  style: shape({}),
};

// One pending GMPRO recommendation on the Proposed tab. Matches the Figma Main
// Card: a white header strip (id + Approve/Reject/Locate) over an #F8F7F9 body
// with the recommended driver/fleet/load and the passenger rows. The Reject
// slide sheet opens over this card.
const GmproGroupingCard = ({
  grouping,
  letter,
  rejecting,
  routeActive,
  onStartReject,
  onCloseReject,
  onApprove,
  onRejectDriver,
  onRejectGrouping,
  onLocate,
}) => {
  const { recommended_user, recommended_vehicle, trip_count, trips = [] } = grouping;
  const driverUnavailable = grouping.status === 'driver_unavailable';

  // Draw the route and centre on this one passenger's pin (not the route hub).
  const locatePassenger = (trip) => {
    onLocate(grouping, pinFor(trip));
  };

  const actionButton = (background) => ({
    background,
    height: 32,
    paddingLeft: 15,
    paddingRight: 15,
    borderRadius: 5,
    fontSize: 16,
  });

  return (
    <div
      data-testid="gmpro-grouping-card"
      className="relative w-full overflow-hidden mb-4"
      style={{
        borderRadius: 15,
        border: `1px solid ${ETA.cardBorder}`,
        boxShadow: '0px 0px 4px 0px rgba(0,0,0,0.15)',
        // Figma renders the card in Ubuntu (a thin typeface). Force it here so
        // the body text doesn't inherit a heavier system fallback at weight 400.
        fontFamily: 'Ubuntu, -apple-system, system-ui, sans-serif',
      }}>
      {rejecting ? (
        <GmproRejectSheet
          grouping={grouping}
          onClose={onCloseReject}
          onRejectDriver={onRejectDriver}
          onRejectGrouping={onRejectGrouping}
        />
      ) : (
        <>
      {/* White header strip: id + actions */}
      <div
        className="bg-white flex items-center justify-between"
        style={{ height: 55, paddingLeft: 18, paddingRight: 18 }}>
        <div className="flex items-center" style={{ gap: 14, minWidth: 0 }}>
          <h2 className="font-bold truncate" style={{ color: ETA.purple, fontSize: 20 }}>
            {formatId(letter, grouping.id)}
          </h2>
          <RouteMetricsBadge
            distanceMeters={grouping.total_distance_meters}
            durationSeconds={grouping.total_duration_seconds}
          />
        </div>
        <div className="flex items-center" style={{ gap: 10, flexShrink: 0 }}>
          <button
            data-testid="gmpro-reject-btn"
            onClick={onStartReject}
            className="flex items-center text-white focus:outline-none"
            style={actionButton(ETA.error)}>
            Reject
          </button>
          <ApproveButton
            disabled={driverUnavailable}
            onClick={() => !driverUnavailable && onApprove(grouping)}
            style={actionButton(ETA.confirm)}
          />
          <button
            data-testid="gmpro-locate-btn"
            title={routeActive ? 'Hide route' : 'View route'}
            onClick={() => onLocate(grouping)}
            className="flex items-center justify-center text-white focus:outline-none"
            style={{
              background: routeActive ? ETA.purple : ETA.grey,
              height: 32,
              width: 32,
              borderRadius: 5,
            }}>
            <FontAwesomeIcon icon={faBullseye} style={{ fontSize: 20 }} />
          </button>
        </div>
      </div>

      {/* Body: recommendation details + passenger rows.
          Two-column grid: column 1 (driver / passenger name) and column 2
          (fleet+occupancy in the header, address in the rows) — the fleet and
          the addresses share the same left edge. */}
      <div style={{ background: ETA.body, padding: '8px 18px 12px' }}>
        {/* Sub-header: recommended driver | fleet + load */}
        <div className="flex items-center" style={{ color: ETA.darkGrey, fontSize: 14 }}>
          <span
            className="flex items-center"
            style={{ gap: 9, width: COL_ONE, flexShrink: 0, minWidth: 0 }}>
            <FontAwesomeIcon icon={faUser} style={{ fontSize: 18, flexShrink: 0 }} />
            <span className="truncate">{recommended_user?.name || 'Unassigned driver'}</span>
          </span>
          <div className="flex items-center justify-between" style={{ flex: 1, minWidth: 0, gap: 12 }}>
            <span className="flex items-center truncate" style={{ gap: 9, minWidth: 0 }}>
              <FontAwesomeIcon icon={faCarSide} style={{ fontSize: 18, flexShrink: 0 }} />
              <span className="truncate">
                {recommended_vehicle?.fleet_name ||
                  recommended_vehicle?.registration_number ||
                  'No fleet'}
              </span>
            </span>
            <span className="flex items-center" style={{ gap: 9, flexShrink: 0 }}>
              <FontAwesomeIcon icon={faUsers} style={{ fontSize: 18 }} />
              {trip_count}/{recommended_vehicle?.capacity ?? '—'}
            </span>
          </div>
        </div>

        <hr style={{ margin: '10px 0', border: 0, borderTop: `1px solid ${ETA.divider}` }} />

        {/* Passenger rows: locate target + name | address */}
        <div className="flex flex-col" style={{ gap: 10 }}>
          {trips.map((trip) => (
            <div
              key={trip.id}
              className="flex items-center"
              style={{ color: ETA.darkGrey, fontSize: 14 }}>
              <div
                className="flex items-center"
                style={{ gap: 9, width: COL_ONE, flexShrink: 0, minWidth: 0 }}>
                <button
                  onClick={() => locatePassenger(trip)}
                  title="Locate passenger"
                  className="flex items-center justify-center focus:outline-none"
                  style={{
                    width: 20,
                    height: 20,
                    flexShrink: 0,
                    border: `1px solid ${ETA.darkGrey}`,
                    borderRadius: 3.125,
                    color: ETA.darkGrey,
                  }}>
                  <FontAwesomeIcon icon={faBullseye} style={{ fontSize: 14 }} />
                </button>
                <span className="truncate">{trip.passenger_name}</span>
              </div>
              <AddressCell address={trip.trip_type === 'outbound' ? trip.destination : trip.location} />
            </div>
          ))}
        </div>
      </div>
        </>
      )}
    </div>
  );
};

GmproGroupingCard.propTypes = {
  grouping: shape({
    id: number,
    total_distance_meters: number,
    total_duration_seconds: number,
    trip_count: number,
    status: string,
    trips: arrayOf(shape({})),
    recommended_user: shape({ name: string }),
    recommended_vehicle: shape({
      fleet_name: string,
      registration_number: string,
      capacity: number,
    }),
  }),
  letter: string,
  rejecting: bool,
  routeActive: bool,
  onStartReject: func,
  onCloseReject: func,
  onApprove: func,
  onRejectDriver: func,
  onRejectGrouping: func,
  onLocate: func,
};

export default GmproGroupingCard;
